import { FC, useContext, useEffect, useState } from "react";
import Modal from "react-modal";
import { XIcon } from "@heroicons/react/solid";
import AuthContext from "../../shared/context/authContext/AuthContext";
import {
    getStripeClinicianProducts,
    getStripeParentProducts,
} from "../../shared/services/stripe/stripe.service";
import Checkout from "../subscription/Checkout";
import { IGetProductResponse } from "../../shared/services/stripe/interfaces/get-products.interfacae";
import { IStripeProduct } from "../../shared/services/stripe/interfaces/product.interface";
import { IStripePrice } from "../../shared/services/stripe/interfaces/price.interface";
import { BusinessHubspotRole } from "../../shared/enums/business-role.enum";
import moment from "moment";
import { CustomManagementRole } from "../../shared/enums/role.enum";
import SubscriptionProducts from "../subscription/SubscriptionProducts";
import B2bSubscriptionEnded from "../subscription/B2bSubscriptionEnded";
import { SubscriptionTierEnum } from "../../shared/enums/SubscriptionTier";
import useUser from "../../shared/hooks/useUser";

interface IProps {
    showModal: boolean;
    skipRoleDescription?: boolean;
    showHeaderWording?: boolean;
    allowCLose?: boolean;
    onClose: (value?: { approved: boolean }) => void;
}

const SubscriptionModal: FC<IProps> = (props) => {
    const { onClose, showModal, skipRoleDescription } = props;
    const [isMobile] = useState(window.innerWidth < 768);
    const [showPrices, setShowPrices] = useState(false);
    const [products, setProducts] = useState<IGetProductResponse[]>([]);
    const [selectedProduct, setSelectedProduct] = useState<{
        product: IStripeProduct;
        price: IStripePrice;
    }>();
    const { state } = useContext(AuthContext);
    const [userRole, setUserRole] = useState(state?.user?.role);
    const isB2cClinician =
        state?.user?.persona === BusinessHubspotRole.B2C_CLINICIAN;
    const isB2bFreeUser =
        state?.user?.group_role && state?.user?.subscriptionTier
            ? [
                  CustomManagementRole.B2B_ACCOUNT_OWNER,
                  CustomManagementRole.B2B_CLINICIAN,
                  CustomManagementRole.B2B_STUDENT,
                  CustomManagementRole.B2B_PARENT,
                  CustomManagementRole.B2B_TEACHER,
              ].includes(state?.user?.group_role) &&
              state?.user?.subscriptionTier === SubscriptionTierEnum.FREE
            : false;
    const { isFreeTrialEnded } = useUser(state?.user);
    const [showCheckoutModal, setShowCheckoutModal] = useState(false);

    useEffect(() => {
        setUserRole(state?.user?.role);
    }, [state]);

    useEffect(() => {
        if (!state?.user) return;

        if (state?.user?.persona === BusinessHubspotRole.B2C_CLINICIAN) {
            getStripeClinicianProducts().then((resp) => {
                setProducts(resp.response);
            });
        } else if (
            state?.user?.group_role === CustomManagementRole.B2C_STUDENT
        ) {
            getStripeParentProducts().then((response) => {
                setProducts(response.response);
            });
        }
    }, [state?.user]);

    const renderProducts = () => (
        <>
            {(showPrices || skipRoleDescription) && (
                <SubscriptionProducts
                    products={products}
                    onSelectProduct={(value) => {
                        setSelectedProduct({
                            product: value.product,
                            price: value.prices[0],
                        });
                        setShowCheckoutModal(true);
                    }}
                    isFreeTrialEnded={isFreeTrialEnded}
                    role={userRole}
                    showHeaderWording={props.showHeaderWording}
                />
            )}
        </>
    );

    return (
        <>
            <Modal
                isOpen={showModal}
                style={{
                    content: {
                        top: "30%",
                        left: "50%",
                        right: "auto",
                        bottom: "auto",
                        marginRight: "-50%",
                        padding: 0,
                        border: 0,
                        transform: "translate(-50%, -30%)",
                        width: "calc(100% - 30px)",
                        height: "auto",
                        background: isB2cClinician ? "#12a9ae" : "#DBEDF2",
                        maxWidth: "1000px",
                        maxHeight: "calc(100vh - 30px)",
                    },
                }}
            >
                <div className="absolute z-10 right-4 top-4 flex gap-5">
                    <button
                        onClick={() => {
                            onClose();
                            setShowPrices(false);
                        }}
                    >
                        <XIcon className="w-6 h-6 text-white" />
                    </button>
                </div>

                {!!(
                    !isB2bFreeUser &&
                    (userRole === "PARENT" ||
                        (userRole === "CLINICIAN" && isB2cClinician))
                ) && renderProducts()}

                {!!isB2bFreeUser && (
                    <B2bSubscriptionEnded
                        organizationName={
                            state?.user?.organization?.organizationName
                        }
                        renewalDate={
                            state?.user?.organization?.renewalDate
                                ? moment(
                                      state?.user?.organization?.renewalDate
                                  ).format("MMM DD, YYYY")
                                : "recently"
                        }
                    />
                )}
            </Modal>

            <Modal
                ariaHideApp={false}
                isOpen={showCheckoutModal}
                onClose={() => setShowCheckoutModal(false)}
                style={{
                    content: {
                        top: "30%",
                        left: "50%",
                        right: "auto",
                        bottom: "auto",
                        marginRight: "-50%",
                        transform: "translate(-50%, -30%)",
                        width: isMobile ? "100%" : 600,
                        padding: 0,
                        border: 0,
                    },
                }}
            >
                {selectedProduct && (
                    <Checkout
                        selectedProduct={selectedProduct}
                        onDismiss={(purchased: boolean) => {
                            setShowCheckoutModal(false);
                            if (purchased) {
                                onClose({ approved: true });
                                setShowPrices(false);
                            }
                        }}
                    />
                )}
            </Modal>
        </>
    );
};

export default SubscriptionModal;
